import { useEffect, useState } from 'react';
import { parse12hClockTo24h, split24hTo12hParts } from '../../utils/timeDisplay';

/**
 * Controlled time input: value/onChange use 24h `HH:mm` (API / DB canonical).
 * UI: 12-hour clock + AM/PM.
 */
export default function TimeInput12h({ value, onChange, disabled, className = '' }) {
  const safeValue = value && String(value).length >= 5 ? String(value).slice(0, 5) : '';
  const initial = split24hTo12hParts(safeValue || '09:00');
  const [hour12, setHour12] = useState(initial.hour12);
  const [minute, setMinute] = useState(initial.minute);
  const [period, setPeriod] = useState(initial.period);

  useEffect(() => {
    const next = split24hTo12hParts(safeValue || '09:00');
    setHour12(next.hour12);
    setMinute(next.minute);
    setPeriod(next.period);
  }, [safeValue]);

  const emit = (h, m, p) => {
    const hhmm = parse12hClockTo24h(h, m, p);
    if (hhmm && typeof onChange === 'function') onChange(hhmm);
  };

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const selectBaseClass =
    'h-10 rounded-lg border border-gray-300 bg-white px-3 pr-8 text-sm font-medium text-gray-900 shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-500';

  return (
    <div className={`inline-flex flex-nowrap items-center gap-2 min-w-0 ${className}`}>
      <select
        aria-label="Hour"
        disabled={disabled}
        value={hour12}
        onChange={(e) => {
          const h = parseInt(e.target.value, 10);
          setHour12(h);
          emit(h, minute, period);
        }}
        className={`${selectBaseClass} w-16 min-w-[4rem]`}
      >
        {hours.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="text-gray-500">:</span>
      <select
        aria-label="Minute"
        disabled={disabled}
        value={minute}
        onChange={(e) => {
          const m = parseInt(e.target.value, 10);
          setMinute(m);
          emit(hour12, m, period);
        }}
        className={`${selectBaseClass} w-16 min-w-[4rem]`}
      >
        {minutes.map((m) => (
          <option key={m} value={m}>
            {String(m).padStart(2, '0')}
          </option>
        ))}
      </select>
      <select
        aria-label="AM or PM"
        disabled={disabled}
        value={period}
        onChange={(e) => {
          const p = e.target.value;
          setPeriod(p);
          emit(hour12, minute, p);
        }}
        className={`${selectBaseClass} w-20 min-w-[5rem]`}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
